import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "./icon";

/**
 * A shared trip document row (TW-3450) — a coloured left accent, a file-type-tinted icon tile, the
 * filename with an optional uploader chip (Travel Connection vs the club), a meta line (type · size ·
 * date), and a trailing actions slot (download / delete). Previously inline in DocumentsTab; extracted
 * so any surface listing documents renders the same row. Styling: `.doc-row` and friends.
 */
export interface DocumentRowProps {
  /** Filename / document title. */
  name: string;
  /** Meta line, already formatted (e.g. "PDF · 1.2 MB · 14 Mar 2026"). */
  meta: string;
  /** Left accent stripe colour (e.g. `var(--primary)` for TC, `var(--info)` for the club). */
  accentColor: string;
  /** File-type tint for the icon tile. */
  fileColor: { bg: string; fg: string };
  /** Icon for the tile (defaults to the generic document glyph). */
  icon?: IconName;
  /** Optional uploader chip. `isBackOffice` picks the TC (building) vs club (user) styling. */
  uploader?: { label: string; isBackOffice: boolean } | null;
  /** Trailing actions (e.g. download / delete `doc-icon-btn` buttons). */
  actions?: ReactNode;
}

export function DocumentRow({
  name,
  meta,
  accentColor,
  fileColor,
  icon = "doc",
  uploader,
  actions,
}: DocumentRowProps) {
  return (
    <div className="doc-row">
      <span className="doc-accent" style={{ background: accentColor }} />
      <span className="doc-file-ic" style={{ background: fileColor.bg, color: fileColor.fg }}>
        <Icon name={icon} size={18} />
      </span>
      <div className="doc-body">
        <div className="doc-title-row">
          <span className="doc-name">{name}</span>
          {uploader && (
            <span className={cn("doc-by", uploader.isBackOffice ? "tc" : "club")}>
              <Icon name={uploader.isBackOffice ? "building" : "user"} size={12} />
              {uploader.label}
            </span>
          )}
        </div>
        <div className="doc-meta">{meta}</div>
      </div>
      {actions && <div className="doc-actions">{actions}</div>}
    </div>
  );
}
